import type { TaskInterval } from '@cognite/apm-client';
import {
  Body,
  Checkbox,
  ConditionIcon,
  DragHandleVerticalIcon,
  EventsIcon,
  Flex,
  GaugeIcon,
  GridIcon,
  HelpIcon,
  Tooltip,
} from '@cognite/cogs.js-v10';
import { TagChip } from '@cognite/cogs.js-v10';
import { Draggable } from '@hello-pangea/dnd';
import { LOCIZE_NAMESPACES, useTranslation } from '@infield/features/i18n';
import { showAssetDetailsAtom } from '@infield/features/template';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useSetRecoilState } from 'recoil';

import {
  readableTimeUntilNextOccurence,
  schedulesToNextOccurrence,
} from '../next-schedule-occurence/utils';

import * as S from './elements';

export type Props = {
  externalId: string;
  index: number;
  assetExternalId?: string;
  assetLabel?: string;
  title?: string;
  isSelected: boolean;
  isChecked: boolean;
  isEditable: boolean;
  hasNumericalMeasurement?: boolean;
  hasLabelMeasurement?: boolean;
  hasIntervals?: boolean;
  hasMessage?: boolean;
  hasCondition?: boolean;
  fadeColor?: boolean;
  schedules?: TaskInterval[];
  isTemplateReady?: boolean;
  onClick?: () => void;
  onClickCheck: () => void;
};

export const TaskListItem: FC<Props> = ({
  externalId,
  index,
  assetExternalId,
  assetLabel,
  title,
  isSelected,
  isChecked,
  isEditable,
  hasNumericalMeasurement,
  hasLabelMeasurement,
  hasIntervals,
  hasMessage,
  hasCondition,
  fadeColor,
  schedules,
  isTemplateReady,
  onClick,
  onClickCheck,
}) => {
  const { t } = useTranslation(LOCIZE_NAMESPACES.template);

  const timeStrings = {
    today: t('nextOccurence.today', 'today'),
    day: t('nextOccurence.day', 'day'),
    days: t('nextOccurence.days', 'days'),
    week: t('nextOccurence.week', 'week'),
    weeks: t('nextOccurence.weeks', 'weeks'),
    month: t('nextOccurence.month', 'month'),
    months: t('nextOccurence.months', 'months'),
    year: t('nextOccurence.year', 'year'),
    years: t('nextOccurence.years', 'years'),
  };

  const firstNextOccurence = schedules
    ? schedulesToNextOccurrence(schedules)
    : undefined;

  const readableNextOccurence = firstNextOccurence
    ? readableTimeUntilNextOccurence(firstNextOccurence, timeStrings)
    : undefined;

  const setShowAssetDetails = useSetRecoilState(showAssetDetailsAtom);
  const handleAssetLinkClicked = (assetExternalId?: string) => {
    if (assetExternalId !== undefined) {
      setShowAssetDetails(true);
    }
  };

  // onClick is not exposed by Checkbox so we are using this hack until it does.
  const checkboxUnwantedProps = {
    onClick: (ev: MouseEvent) => ev.stopPropagation(),
  } as any;

  return (
    <Draggable
      key={externalId}
      isDragDisabled={!isEditable}
      draggableId={externalId}
      index={index}
    >
      {(provided, { isDragging }) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <S.Wrapper
            data-testid="template-task-list-item"
            $isSelected={isSelected}
            onClick={onClick}
            $fadeColor={fadeColor}
            $isDragging={isDragging}
            $isEditable={isEditable}
          >
            <S.Content>
              <Flex gap={8} alignItems="center">
                {isEditable && (
                  <>
                    <Checkbox
                      {...checkboxUnwantedProps}
                      checked={isChecked}
                      onChange={onClickCheck}
                    />
                    <TagChip
                      colorScheme="blue"
                      label=""
                      icon={<DragHandleVerticalIcon />}
                      size="x-small"
                    />
                  </>
                )}
                {assetLabel && (
                  <S.Link
                    onClick={() => handleAssetLinkClicked(assetExternalId)}
                  >
                    {assetLabel}
                  </S.Link>
                )}
                <Body strong size="medium">
                  {title}
                </Body>
              </Flex>
              <Flex gap={8}>
                {hasNumericalMeasurement && (
                  <TagChip
                    colorScheme="blue"
                    label=""
                    icon={<GaugeIcon />}
                    size="x-small"
                  />
                )}
                {hasLabelMeasurement && (
                  <TagChip
                    colorScheme="blue"
                    label=""
                    icon={<GridIcon />}
                    size="x-small"
                  />
                )}
                {hasMessage && (
                  <TagChip
                    colorScheme="blue"
                    label=""
                    icon={<HelpIcon />}
                    size="x-small"
                  />
                )}
                {hasCondition && (
                  <TagChip
                    colorScheme="blue"
                    label=""
                    icon={<ConditionIcon />}
                    size="x-small"
                  />
                )}
                {hasIntervals && (
                  <Tooltip
                    // Format like "Fri, Jul 07 2023"
                    content={dayjs(firstNextOccurence).format(
                      'ddd, MMM DD YYYY'
                    )}
                    disabled={!firstNextOccurence || !isTemplateReady}
                  >
                    <TagChip
                      data-testid="task-list-item-next-occurence"
                      label={
                        readableNextOccurence && isTemplateReady
                          ? readableNextOccurence
                          : ''
                      }
                      icon={<EventsIcon />}
                      size="x-small"
                      colorScheme="blue"
                    />
                  </Tooltip>
                )}
              </Flex>
            </S.Content>
          </S.Wrapper>
        </div>
      )}
    </Draggable>
  );
};
